<template>
    <div class="overflow-x-auto">
        <table
            :class="[
                'min-w-full divide-y divide-theme-border bg-theme-card text-theme-base',
                outlined ? 'border border-theme-border' : ''
            ]"
        >
            <thead class="bg-theme-hover">
                <tr>
                    <th
                        v-for="field in fields"
                        :key="field.key"
                        :class="[
                            'xl:px-4 lg:px-3 px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-theme-base',
                            ,
                            field.thClass,
                            { 'sortable-header': field.sortable }
                        ]"
                        :role="field.sortable ? 'columnheader' : null"
                        :scope="'col'"
                        :tabindex="field.sortable ? '0' : null"
                        :aria-colindex="field.colIndex"
                        :aria-sort="getAriaSort(field.key)"
                        @click="field.sortable ? toggleSort(field.key) : null"
                        @keydown.enter="
                            field.sortable ? toggleSort(field.key) : null
                        "
                        @keydown.space.prevent="
                            field.sortable ? toggleSort(field.key) : null
                        "
                    >
                        <div class="flex items-center justify-between">
                            <span class="text-bold">{{ field.label }}</span>
                            <span
                                v-if="field.sortable"
                                class="sort-icons ml-1"
                                :class="{ active: sortBy === field.key }"
                            >
                                <span
                                    v-if="sortBy !== field.key"
                                    class="sort-icon-none"
                                >
                                    ⇅
                                </span>
                                <span
                                    v-else-if="sortDesc"
                                    class="sort-icon-desc"
                                >
                                    ▼
                                </span>
                                <span v-else class="sort-icon-asc"> ▲ </span>
                            </span>
                        </div>
                        <span v-if="field.sortable" class="sr-only">
                            {{ getSrOnlyText(field.key) }}
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, index) in sortedItems"
                    :key="index"
                    :class="[
                        'h-14 bg-table-row-theme hover:bg-theme-hover',
                        {
                            'border-2 bg-theme-selected border-theme-primary':
                                selectedIndex.includes(index)
                        },
                        selectable ? 'cursor-pointer' : ''
                    ]"
                    @click="selectable ? selectRow(item, index) : null"
                >
                    <td
                        v-for="field in fields"
                        :key="field.key"
                        :class="[
                            'xl:px-4 lg:px-3 px-2 py-2 whitespace-nowrap text-sm text-theme-base',
                            field.tdClass
                        ]"
                    >
                        <slot
                            :name="`cell(${field.key})`"
                            :item="item"
                            :index="index"
                            :value="item[field.key]"
                        >
                            {{ item[field.key] }}
                        </slot>
                    </td>
                </tr>
                <tr v-if="sortedItems.length === 0">
                    <td
                        :colspan="fields.length"
                        :class="['text-center py-4 text-sm text-theme-base']"
                    >
                        {{ itemsEmptyMessage }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    name: "GenericTable",
    events: ["flex-selected", "sort-changed"],
    props: {
        fields: {
            type: Array,
            required: true
            // Format: [{ key: 'slug', label: 'Slug', sortable: true, thClass: '', tdClass: '' }]
        },
        items: {
            type: Array,
            required: true
        },
        outlined: {
            type: Boolean,
            default: false
        },
        responsive: {
            type: Boolean,
            default: true
        },
        selectable: {
            type: Boolean,
            default: false
        },
        selectMode: {
            type: String,
            default: "single",
            validator: (value) => ["single", "multi"].includes(value)
        },
        perPage: {
            type: Number,
            default: 0
        },
        sortByProp: {
            type: String,
            default: null
        },
        sortDescProp: {
            type: Boolean,
            default: false
        },
        itemsEmptyMessage: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            selectedIndex: [],
            selectedRows: [],
            sortBy: this.sortByProp,
            sortDesc: this.sortDescProp
        };
    },
    computed: {
        paginatedItems() {
            if (this.perPage === 0) {
                return this.items;
            }
            return this.items.slice(0, this.perPage);
        },
        sortedItems() {
            const items = [...this.paginatedItems];

            if (!this.sortBy) {
                return items;
            }

            return items.sort((a, b) => {
                const aVal = a[this.sortBy];
                const bVal = b[this.sortBy];

                // Handle null/undefined
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;

                // Compare values
                let result = 0;
                if (typeof aVal === "number" && typeof bVal === "number") {
                    result = aVal - bVal;
                } else {
                    result = String(aVal).localeCompare(String(bVal));
                }

                return this.sortDesc ? -result : result;
            });
        }
    },
    methods: {
        toggleSort(fieldKey) {
            if (this.sortBy === fieldKey) {
                // Toggle entre asc, desc, y sin ordenar
                if (!this.sortDesc) {
                    this.sortDesc = true;
                } else {
                    this.sortBy = null;
                    this.sortDesc = false;
                }
            } else {
                this.sortBy = fieldKey;
                this.sortDesc = false;
            }

            this.$emit("sort-changed", {
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            });
        },
        getAriaSort(fieldKey) {
            if (this.sortBy !== fieldKey) return "none";
            return this.sortDesc ? "descending" : "ascending";
        },
        getSrOnlyText(fieldKey) {
            if (this.sortBy !== fieldKey) {
                return "(Click to sort ascending)";
            }
            if (this.sortDesc) {
                return "(Click to clear sorting)";
            }
            return "(Click to sort descending)";
        },
        selectRow(item, index) {
            if (!this.selectable) return;

            if (this.selectMode === "single") {
                this.selectedIndex = this.selectedIndex.includes(index)
                    ? []
                    : [index];
                this.selectedRows = this.selectedIndex.length > 0 ? [item] : [];
            } else {
                // Multi-select
                const selectedIdx = this.selectedRows.findIndex(
                    (flex) => flex === item
                );
                if (selectedIdx > -1) {
                    this.selectedRows.splice(selectedIdx, 1);
                    this.selectedIndex.splice(
                        this.selectedIndex.indexOf(index),
                        1
                    );
                } else {
                    this.selectedRows.push(item);
                    this.selectedIndex.push(index);
                }
            }

            this.$emit("flex-selected", this.selectedRows);
        },
        clearSelected() {
            this.selectedIndex = [];
            this.selectedRows = [];
        }
    }
};
</script>

<style scoped>
.sort-icons {
    @apply inline-flex opacity-30 text-xs transition-opacity duration-200;
}

.sortable-header:hover .sort-icons {
    @apply opacity-60;
}

.sort-icons.active {
    @apply opacity-100;
}
</style>
